import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import {
  Subject,
  Subscription,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs';
import { ToastService } from '../../../services/toast/toast.service';
import { IEnvironment } from '../../../models/environment.model';
import { ValidationErrors, AbstractControl } from '@angular/forms';
import { formatFileSizeBytesUtil } from '../../../../utils/utils/base-64-uri-to-file-converter.util';
import { IIremboFileUpload } from '../../../models/irembo-file-upload-response.model';
import { KeycloakService } from 'keycloak-angular';
import { IPreviousAttachmentEvent } from '../../../models/previous-attachment-event';
import { ICertificateAttachmentEvent } from '../../../models/certificate-attachment-event';
import { UserDocumentType } from '../../../models/user-document-type.enum';
import { FileUploadSource } from '../../../models/file-upload-source.enum';
import { getCertificateNameByLocale } from '../../../../utils/utils/application-form.util';
import { VirusScanStatus } from '../../../../models/virus-scan.model';
import {
  findFormControl,
  findFormControlInScope,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormStateService } from '../../../services/formly/form-state.service';
import { TranslateService } from '@ngx-translate/core';

const FILEMANAGER_URL = '/application/v1/file-manager/application/attachment';
const CERTIFICATE_URL = '/document-generation/v1/file-manager/certificate';

interface IInternalFileUpload {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileDownloadName: string;
  content: string;
  source: FileUploadSource;
  uploadResponse?: any;
  fileHash?: string;
}

@Component({
  selector: 'irembogov-custom-internal-file-upload',
  templateUrl: './custom-internal-file-upload.component.html',
})
export class CustomInternalFileUploadComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  private subscriptions = new Subscription();
  keepAliveInterval?: any;

  noFileActionLabel = 'Click to upload';
  noFileSubLabelHtml = 'or drag and drop';

  private _uploadStatus:
    | 'idle'
    | 'uploading'
    | 'completed'
    | 'error'
    | 'deleting' = 'idle';

  get uploadStatus() {
    return this._uploadStatus;
  }

  set uploadStatus(
    value: 'idle' | 'uploading' | 'completed' | 'error' | 'deleting'
  ) {
    this._uploadStatus = value;
  }
  progress = new Subject<number>();
  errorMessage?: string;
  uploadedFile?: IInternalFileUpload;
  currentProgressValue = 0;
  deleteInProgress = false;

  private environment: IEnvironment;

  // Properties matching custom-file-upload exactly
  maxSizeText = 'max.';
  totalPreviousAttachments = 0;
  totalCertificateAttachments = 0;
  previousAttachments: IPreviousAttachmentEvent[] = [];
  certificateAttachments: ICertificateAttachmentEvent[] = [];
  filePath: string | undefined;
  loadingFile = false;
  allowUploadPreviousFiles = true;

  page = 0;
  size = 10;
  sort = 'dateCreated,asc';
  placeholder = 'Select file to upload';
  dataField = 'data.content';

  file: IIremboFileUpload | null | undefined;
  photoValidationResponse: Record<string, unknown> | undefined;
  validatingPhoto = false;
  duplicateFile = false;

  // Properties from base component for validation checking
  hasErrors = false;
  invalidPhoto = false;
  fileAttachmentNotValid = false;
  scanningForVirus = false;
  virusScanStatus: VirusScanStatus = VirusScanStatus.NOT_SCANNED;
  private fileHashes = new Map<string, string>();

  constructor(
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    private toastService: ToastService,
    private keycloakService: KeycloakService,
    private modalService: NgbModal,
    @Inject('environment') environment: IEnvironment,
    private readonly formStateService: FormStateService,
    private readonly translateService: TranslateService
  ) {
    super();
    this.environment = environment;
    this.allowUploadPreviousFiles =
      this.environment.allowUploadPreviousFiles ?? true;
  }

  ngOnInit() {
    this.updateNoFileSubLabelHtml();
    this.setupFileRemovalListener();
    this.setupValidationListener();
    this.setupValueRestorationListener();
    if (this.field.formControl?.value && !this.file) {
      this.restoreFileFromValue(this.field.formControl.value);
    }
    this.updateFormValidation();
  }

  private setupFileRemovalListener() {
    // Listen for form control value changes to detect file removal
    this.subscriptions.add(
      this.field.formControl?.valueChanges.subscribe(value => {
        // If value changes from having a file to null, it was removed
        if (value === null && this.file) {
          this.onFileRemoved();
        }
      })
    );
  }

  private setupValidationListener() {
    // Update validation whenever upload status changes (but not too frequently)
    this.subscriptions.add(
      this.progress
        .pipe(
          distinctUntilChanged(),
          debounceTime(100) // Limit validation updates during rapid progress changes
        )
        .subscribe(() => {
          // Only update validation if not in active upload/delete state
          // to prevent interfering with the upload process
          if (
            this.uploadStatus !== 'uploading' &&
            this.uploadStatus !== 'deleting'
          ) {
            this.updateFormValidation();
          }
        })
    );
  }

  private setupValueRestorationListener() {
    // Listen for form control setValue events to restore uploaded file state
    // This ensures the UI doesn't reset when navigating between steps
    this.subscriptions.add(
      this.field.formControl?.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(value => {
          // If value is set but we don't have file data, restore from value
          if (value && !this.file && value.fileName) {
            this.restoreFileFromValue(value);
          }
        })
    );
  }

  private restoreFileFromValue(value: IInternalFileUpload) {
    // If the value has 'content', it is a fresh local file being uploaded
    // We must return immediately so the active upload flow isn't interrupted!
    if (value?.content) {
      return;
    }

    // If the base component detected a bad format or size
    // load the 'content'. However, We must return immediately so the UI can keep the file card and display these errors!
    const errors = this.field.formControl?.errors || {};
    if (
      errors['invalidfileformat'] ||
      errors['maximumUploadSize'] ||
      errors['minimumUploadSize']
    ) {
      return;
    }

    // No fileDownloadName = upload never completed; refuse to restore it
    // as 'completed' so the bad record cannot reach Submit.
    if (!value?.fileDownloadName) {
      this.field.formControl?.setValue(null, { emitEvent: false });
      this.field.formControl?.setErrors(null);
      this.uploadedFile = undefined;
      this.uploadStatus = 'idle';
      this.file = undefined;
      this.updateFormValidation();
      this.cd.detectChanges();
      return;
    }

    // If the backend draft handed us a hash, rehydrate our local and global memory!
    if (value.fileHash && !this.field.props['disableDuplicateValidation']) {
      this.fileHashes.set(value.fileName, value.fileHash);

      // Ensure we don't accidentally push it twice
      if (!this.formStateService.isDuplicateFile(value.fileHash)) {
        this.formStateService.saveHashAttachmentInArray(
          value.fileName,
          value.fileHash
        );
      }
    }

    this.uploadedFile = value;
    this.uploadStatus = 'completed';
    this.file = {
      fileName: value.fileName,
      fileSize: value.fileSize,
      fileType: value.fileType,
      fileDownloadName: value.fileDownloadName,
      content: value.content || '',
    } as IIremboFileUpload;

    this.cd.detectChanges();
  }

  private updateFormValidation() {
    if (!this.field.formControl) {
      return;
    }

    const isRequired = this.field.props?.['required'] === true;
    const currentErrors = this.field.formControl.errors || {};

    if (this.uploadStatus === 'uploading' || this.uploadStatus === 'deleting') {
      // During upload/delete operations, field is always invalid (required or optional)
      const newErrors = {
        ...currentErrors,
        uploadInProgress: {
          message: this.translateService.instant(
            'Upload in progress, please wait...'
          ),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (this.uploadStatus === 'completed' && this.uploadedFile) {
      // Upload completed successfully, remove upload-specific errors
      const { uploadInProgress, uploadError, ...otherErrors } = currentErrors;
      const hasOtherErrors = Object.keys(otherErrors).length > 0;
      const newErrors = hasOtherErrors ? otherErrors : null;
      this.field.formControl.setErrors(newErrors);
    } else if (this.uploadStatus === 'error') {
      // Upload failed, field remains invalid
      const newErrors = {
        ...currentErrors,
        uploadError: {
          message:
            this.errorMessage || this.translateService.instant('Upload failed'),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (
      this.uploadStatus === 'idle' &&
      !this.uploadedFile &&
      isRequired
    ) {
      // No file uploaded yet, field is invalid only if originally required
      const newErrors = {
        ...currentErrors,
        required: {
          message: this.translateService.instant('This field is required'),
        },
      };
      this.field.formControl.setErrors(newErrors);
    } else if (
      this.uploadStatus === 'idle' &&
      !this.uploadedFile &&
      !isRequired
    ) {
      // Optional field with no upload - remove upload-related errors but keep others
      const { uploadInProgress, uploadError, required, ...otherErrors } =
        currentErrors;
      const hasOtherErrors = Object.keys(otherErrors).length > 0;
      const newErrors = hasOtherErrors ? otherErrors : null;
      this.field.formControl.setErrors(newErrors);
    }

    this.field.formControl.markAsTouched();
  }

  private updateNoFileSubLabelHtml(): void {
    this.noFileSubLabelHtml = 'or drag and drop <br />';

    const validation = this.field.props?.['validation'];
    if (!validation) return;

    let fileFormats = '';
    if (
      validation['allowedFormats'] &&
      Array.isArray(validation['allowedFormats'])
    ) {
      fileFormats = validation['allowedFormats'].join(', ').toUpperCase();
    }

    const sizeLimits: string[] = [];

    this.calculateSizeLimit(
      validation['maximumUploadSize'],
      sizeLimits,
      'max. '
    );

    this.calculateSizeLimit(
      validation['minimumUploadSize'],
      sizeLimits,
      'min. '
    );

    this.noFileSubLabelHtml = `or drag and drop <br />${fileFormats}${
      sizeLimits.length > 0 ? '(' + sizeLimits.join(',') + ')' : ''
    }`;
    this.cd.detectChanges();
  }

  private calculateSizeLimit(
    size: number | undefined,
    sizeLimits: string[],
    prepend?: string
  ): void {
    if (size && !Number.isNaN(+size)) {
      sizeLimits.push(prepend + formatFileSizeBytesUtil(+size * 1024, 0));
    }
  }

  onFileUploadEvent(file: IIremboFileUpload) {
    // Memorize the hash for DEVICE uploads before the content is wiped
    if (
      file.content &&
      typeof file.content === 'string' &&
      !this.field.props['disableDuplicateValidation']
    ) {
      this.formStateService.computeSHA256(file.content).then(hash => {
        if (file.fileName) {
          this.fileHashes.set(file.fileName, hash);
        }
      });
    }
    // Handle standard attachment logic first (like custom-file-upload)
    const isImage = file['fileType'].includes('image');
    this.photoValidationResponse = {};
    this.file = file;

    if (this.field.props['validatePhoto'] && file['content'] && isImage) {
      const fileContent = file.content as string;
      const base64Str = fileContent.replace(/^data:image\/[a-z]+;base64,/, '');
      this.validatingPhoto = true;
      this.validatePhoto(base64Str).subscribe({
        next: res => {
          const checks = res['checks'] as Record<string, string>;
          this.photoValidationResponse = { ...checks, has_checks: true };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
        error: () => {
          this.photoValidationResponse = { has_checks: false };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
      });
    }

    const internalFile: IInternalFileUpload = {
      fileName: file.fileName,
      fileSize: file.fileSize,
      fileType: file.fileType,
      fileDownloadName: file.fileDownloadName,
      content: typeof file.content === 'string' ? file.content : '',
      source: FileUploadSource.DEVICE,
      uploadResponse: undefined,
    };

    // The base component validation hasn't run yet when this event fires
    // We need to wait for the next tick to allow base component validation to complete
    setTimeout(() => {
      this.checkValidationAndProceed(internalFile);
    }, 0);

    this.cd.detectChanges();
  }

  async onFileRemoved() {
    // Remove the file from the duplicate validation hash array
    if (
      this.file &&
      this.file.fileName &&
      !this.duplicateFile &&
      !this.field.props['disableDuplicateValidation']
    ) {
      // Fetch the exact hash tied to this specific file name
      let hashToRemove = this.fileHashes.get(this.file.fileName);

      // If it's a local device file, it might not be in the Map yet,
      // but it still has its Base64 content, so we can just compute it!
      if (!hashToRemove && this.file.content) {
        hashToRemove = await this.formStateService.computeSHA256(
          this.file.content as string
        );
      }

      if (hashToRemove) {
        // Remove from the global service
        if (
          typeof this.formStateService.removeHashAttachmentFromArray ===
          'function'
        ) {
          this.formStateService.removeHashAttachmentFromArray(
            this.file.fileName,
            hashToRemove
          );
        }
        // Clean up our local Map to prevent memory leaks
        this.fileHashes.delete(this.file.fileName);
      }
    }

    // Wipe the component state
    this.file = undefined;
    this.duplicateFile = false;
    this.resetComponentState();
  }

  private resetComponentState() {
    this.uploadStatus = 'idle';
    this.uploadedFile = undefined;
    this.errorMessage = undefined;
    this.currentProgressValue = 0;
    this.progress.next(0);
  }

  resetToIdle() {
    this.uploadStatus = 'idle';
    this.errorMessage = undefined;
    this.currentProgressValue = 0;
    this.progress.next(0);
  }

  /**
   * Check validation status and proceed with internal upload if valid
   */
  private checkValidationAndProceed(internalFile: IInternalFileUpload) {
    this.errorMessage = undefined;

    const hasValidationErrors = this.hasFileValidationErrors();

    if (!hasValidationErrors) {
      // File passed all validations, proceed with internal upload
      this.uploadedFile = internalFile;
      this.startInternalUpload(internalFile);
    } else {
      // File validation failed, don't proceed with internal upload
      this.uploadedFile = undefined;
      this.uploadStatus = 'error';
      this.updateFormValidation();
    }

    this.cd.detectChanges();
  }

  /**
   * Check if the base file upload component has validation errors
   * Uses the form control validation for reliable validation checking
   */
  private hasFileValidationErrors(): boolean {
    if (!this.file) {
      return true; // No file means validation failed
    }

    // Use form control validation as the most reliable method
    if (this.field.formControl) {
      // Check current errors without triggering validation
      const errors = this.field.formControl.errors;

      // Check for validation errors that should prevent internal upload
      if (errors) {
        const blockingErrors = [
          'invalidfileformat',
          'minimumUploadSize',
          'maximumUploadSize',
          'duplicateAttachment',
          'invalidPhoto',
          'virusDetected',
          'virusScanError',
          'notUniqueDataInAttachment',
        ];

        return blockingErrors.some(errorKey => errors[errorKey]);
      }
    }

    // Fallback to checking individual properties if form control approach fails
    return (
      this.hasErrors ||
      this.invalidPhoto ||
      this.duplicateFile ||
      this.fileAttachmentNotValid ||
      this.validatingPhoto ||
      this.scanningForVirus ||
      this.virusScanStatus === VirusScanStatus.INFECTED ||
      this.virusScanStatus === VirusScanStatus.ERROR
    );
  }

  private startInternalUpload(file: IInternalFileUpload) {
    this.uploadStatus = 'uploading';
    this.errorMessage = undefined;
    this.currentProgressValue = 0;

    // Set form as invalid during upload to prevent submission
    this.updateFormValidation();

    // Show initial progress
    this.progress.next(0);
    this.cd.detectChanges();

    // Start session keep-alive if enabled
    this.startSessionKeepAlive();

    // Simulate initial preparation phase
    this.progress.next(5);

    // Direct upload to internal endpoint
    this.performInternalUpload(file);
  }

  private performInternalUpload(file: IInternalFileUpload) {
    const uploadUrl = `${this.environment.apiGatewayBaseUrl}/application/v1/application-attachments/upload`;

    // Prepare the request body as FormData
    const formData = new FormData();

    // Add the file
    if (file.content) {
      const blob = this.base64ToBlob(file.content, file.fileType);
      formData.append('file', blob, file.fileName);
    }

    this.subscriptions.add(
      this.http
        .post(uploadUrl, formData, {
          reportProgress: true,
          observe: 'events',
        })
        .subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress) {
              if (event.total) {
                // Calculate progress with base offset (5% for preparation)
                const uploadProgress = Math.round(
                  (85 * event.loaded) / event.total
                );
                const totalProgress = 5 + uploadProgress;
                this.currentProgressValue = Math.min(totalProgress, 100);
                this.progress.next(this.currentProgressValue);

                // Ensure form stays invalid during progress updates
                if (
                  this.uploadStatus === 'uploading' &&
                  this.field.formControl &&
                  this.field.formControl.valid
                ) {
                  const newErrors = {
                    ...this.field.formControl.errors,
                    uploadInProgress: {
                      message: this.translateService.instant(
                        'Upload in progress, please wait...'
                      ),
                    },
                  };
                  this.field.formControl.setErrors(newErrors);
                }

                this.cd.detectChanges();
              }
            } else if (event.type === HttpEventType.Response) {
              // Upload complete, show processing
              this.progress.next(100);
              this.cd.detectChanges();
              this.handleUploadSuccess(event.body);
            }
          },
          error: error => {
            this.handleUploadError(error);
          },
        })
    );
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    // Remove data URL prefix if present
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  private handleUploadSuccess(response: any) {
    // Ensure progress shows 100% briefly before completing
    this.progress.next(100);
    // Emit on the committed HttpResponse, before the delayed setTimeout body
    // (which does unguarded response.data parsing / may be skipped on destroy).
    this.emitFetchOutcome('success');

    setTimeout(() => {
      this.uploadStatus = 'completed';
      this.stopSessionKeepAlive();

      if (this.uploadedFile) {
        // Create a new file object with empty content for internal uploads
        // The file is now stored in temp bucket, so we only keep metadata and fileDownloadName
        this.uploadedFile = {
          ...this.uploadedFile,
          content: '',
          fileDownloadName: response.data.fileDownloadName,
          uploadResponse: response,
          fileHash: this.fileHashes.get(this.uploadedFile.fileName),
        };

        // Set this.file for UI display
        this.file = {
          fileName: this.uploadedFile.fileName,
          fileSize: this.uploadedFile.fileSize,
          fileType: this.uploadedFile.fileType,
          fileDownloadName: this.uploadedFile.fileDownloadName,
          content: '',
        } as IIremboFileUpload;
      }

      // Update form validation first, before setValue
      this.updateFormValidation();

      // Set the field value after validation is set (to avoid clearing errors)
      // Store metadata and fileDownloadName, not the file content
      this.field.formControl?.setValue(this.uploadedFile, { emitEvent: false });

      this.toastService.show({
        body: this.translateService.instant('File uploaded successfully'),
        type: 'success',
      });

      this.cd.detectChanges();
    }, 200); // Brief delay to show 100% completion
  }

  private handleUploadError(error: any) {
    this.stopSessionKeepAlive();

    const errorMessage =
      error.error?.message ||
      error.message ||
      this.translateService.instant('Upload failed');

    this.toastService.show({
      body: errorMessage,
      type: 'error',
    });
    this.emitFetchOutcome('failure', error, errorMessage);

    // Clear the form control so no broken record can be persisted by Save
    // Draft or sent at Submit. emitEvent: false avoids retriggering the
    // removal / restoration listeners we are cleaning up manually here.
    this.resetComponentState();
    this.field.formControl?.setValue(null, { emitEvent: false });
    this.field.formControl?.setErrors(null);
    this.file = undefined;
    this.updateFormValidation();
  }

  private setError(message: string) {
    this.uploadStatus = 'error';
    this.errorMessage = message;
    this.field.formControl?.setErrors({ uploadError: { message } });
    this.cd.detectChanges();
  }

  private startSessionKeepAlive() {
    const sessionConfig = this.field.props?.['sessionKeepAlive'];
    if (sessionConfig?.enableKeepAlive) {
      const interval = sessionConfig.keepAliveInterval || 60000;

      this.keepAliveInterval = setInterval(() => {
        this.refreshAuthToken();
      }, interval);
    }
  }

  private stopSessionKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = undefined;
    }
  }

  private refreshAuthToken() {
    // Silent token refresh - completely invisible to user
    this.keycloakService
      .updateToken(60) // Refresh if expires within 60 seconds
      .then(() => {
        // Silent success - no logging, no user feedback
      })
      .catch(() => {
        // Try forced refresh silently
        this.keycloakService
          .updateToken(-1) // Force refresh
          .then(() => {
            // Silent success
          })
          .catch(() => {
            // Silent failure - let upload continue
          });
      });
  }

  handleFileValidationErrors(errors: ValidationErrors) {
    if (errors) {
      const errorKeys = Object.keys(errors);
      const firstError = errors[errorKeys[0]];
      this.setError(
        firstError?.message ||
          this.translateService.instant('File validation failed')
      );
    }
  }

  // Additional event handlers for full compatibility
  onSelectFile() {
    // Load user documents if not already loaded
    if (this.previousAttachments.length === 0) {
      const { dataset } = this.field.props;
      if (dataset?.['params']) {
        const externalParams = dataset['params'] as Record<string, unknown>;
        if (
          externalParams['size'] &&
          typeof externalParams['size'] === 'number'
        ) {
          this.size = externalParams['size'];
        }
        if (
          externalParams['sort'] &&
          typeof externalParams['sort'] === 'string'
        ) {
          this.sort = externalParams['sort'];
        }
      }

      this.loadUserDocuments(
        '/application/v1/application/find-user-certificates',
        UserDocumentType.CERTIFICATES
      );
      this.loadUserDocuments(
        '/application/v1/application/find-user-previous-attachments',
        UserDocumentType.PREVIOUS_ATTACHMENTS
      );
    }
  }

  selectPreviousAttachmentEvent(event: IPreviousAttachmentEvent) {
    // Close the modal first, just like in custom-file-upload
    this.modalService.dismissAll();

    // Handle previous attachment selection - upload to internal service
    this.handleAttachmentSelection(event, 'previous');
  }

  selectCertificateAttachmentEvent(event: ICertificateAttachmentEvent) {
    // Close the modal first, just like in custom-file-upload
    this.modalService.dismissAll();

    // Handle certificate attachment selection - upload to internal service
    this.handleAttachmentSelection(event, 'certificate');
  }

  private handleAttachmentSelection(
    event: IPreviousAttachmentEvent | ICertificateAttachmentEvent,
    type: 'previous' | 'certificate'
  ) {
    // First, we need to load the actual file content from the server
    // This mimics what the regular file upload component does
    this.loadingFile = true;
    this.cd.detectChanges();

    const fileName =
      type === 'previous'
        ? (event as IPreviousAttachmentEvent).fileOriginalName
        : getCertificateNameByLocale(
            (event as ICertificateAttachmentEvent).certificateNames
          );

    const fileDownloadName =
      type === 'previous'
        ? (event as IPreviousAttachmentEvent).fileName
        : getCertificateNameByLocale(
            (event as ICertificateAttachmentEvent).certificateNames
          );

    if (type === 'previous') {
      // Handle previous attachment - use the same approach as custom-file-upload
      const fileManagerUrl = `${this.environment.apiGatewayBaseUrl}${FILEMANAGER_URL}`;
      this.subscriptions.add(
        this.getBlob(
          {
            id: (event as IPreviousAttachmentEvent).applicationId,
            fileName: (event as IPreviousAttachmentEvent).fileName,
          },
          fileManagerUrl
        ).subscribe({
          next: (httpEvent: HttpEvent<Blob>) => {
            if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
              this.convertBlobToBase64(
                httpEvent.body,
                fileName,
                fileDownloadName,
                FileUploadSource.PREVIOUS_ATTACHMENT
              );
            }
          },
          error: () => {
            this.loadingFile = false;
            this.toastService.show({
              body: this.translateService.instant(
                'Failed to load selected file'
              ),
              type: 'error',
            });
            this.cd.detectChanges();
          },
        })
      );
    } else {
      // Handle certificate attachment
      const certificateUrl = `${this.environment.apiGatewayBaseUrl}${CERTIFICATE_URL}`;
      this.subscriptions.add(
        this.getBlob(
          {
            certificateName: getCertificateNameByLocale(
              (event as ICertificateAttachmentEvent).certificateNames
            ),
          },
          certificateUrl
        ).subscribe({
          next: (httpEvent: HttpEvent<Blob>) => {
            if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
              this.convertBlobToBase64(
                httpEvent.body,
                fileName,
                fileDownloadName,
                FileUploadSource.CERTIFICATE
              );
            }
          },
          error: () => {
            this.loadingFile = false;
            this.toastService.show({
              body: this.translateService.instant(
                'Failed to load selected file'
              ),
              type: 'error',
            });
            this.cd.detectChanges();
          },
        })
      );
    }
  }

  private checkIfFileHasExistingLink(fileDownloadName: string): boolean {
    // Server-generated fileDownloadNames typically:
    // - Have UUIDs or long random strings
    // - Are longer than typical user filenames
    // - Follow specific patterns from our file manager

    // Heuristic: if fileDownloadName is long (>20 chars) and looks server-generated
    return !!(
      fileDownloadName &&
      fileDownloadName.length > 20 &&
      /[A-Z0-9]{8,}/.test(fileDownloadName)
    );
  }

  private reuseExistingFileLink(
    blob: Blob,
    fileName: string,
    fileDownloadName: string
  ) {
    const fileType = blob.type || this.inferMimeTypeFromFileName(fileName);

    // Set completed state without upload
    this.uploadStatus = 'completed';
    this.uploadedFile = {
      fileName,
      fileSize: blob.size,
      fileType,
      fileDownloadName, // Use existing link
      content: '', // No content needed
      source: FileUploadSource.PREVIOUS_ATTACHMENT,
      uploadResponse: { data: { fileDownloadName } },
      fileHash: this.fileHashes.get(fileName),
    };

    // Set file for UI display
    this.file = {
      fileName,
      fileSize: blob.size,
      fileType,
      fileDownloadName,
      content: '',
    } as IIremboFileUpload;

    this.loadingFile = false;
    this.updateFormValidation();
    this.field.formControl?.setValue(this.uploadedFile, { emitEvent: false });

    this.toastService.show({
      body: this.translateService.instant(
        'File selected from previous attachments'
      ),
      type: 'success',
    });

    this.cd.detectChanges();
  }

  private convertBlobToBase64(
    blob: Blob,
    fileName: string,
    fileDownloadName: string,
    source: FileUploadSource = FileUploadSource.DEVICE
  ) {
    const reader = new FileReader();

    reader.onload = async () => {
      const base64Content = reader.result as string;
      const fileType = blob.type || this.inferMimeTypeFromFileName(fileName);

      // Create the UI file object and assign it
      this.file = {
        fileName: fileName,
        fileSize: blob.size,
        fileType: fileType,
        fileDownloadName: fileDownloadName,
        content: base64Content,
        source: source,
      } as IIremboFileUpload;

      // This ensures the base component renders the file card so the duplicate error
      // has something to attach to (preventing the generic "Field is required" message).
      this.field.formControl?.setValue(this.file);
      this.loadingFile = false;
      this.cd.detectChanges();

      // DUPLICATE FILE VALIDATION LOGIC
      if (!this.field.props['disableDuplicateValidation']) {
        const fileHash = await this.formStateService.computeSHA256(
          base64Content
        );

        if (this.formStateService.isDuplicateFile(fileHash)) {
          // Flag as duplicate, set error state, and stop processing
          this.duplicateFile = true;
          this.uploadStatus = 'error';
          this.errorMessage = this.translateService.instant(
            'Duplicate file detected.'
          );
          this.updateFormValidation();
          this.cd.detectChanges();
          return;
        }

        // Not a duplicate, save hash
        this.fileHashes.set(fileName, fileHash);
        this.formStateService.saveHashAttachmentInArray(fileName, fileHash);
        this.duplicateFile = false;
      }

      // Proceed with normal internal upload logic
      const hasExistingLink = this.checkIfFileHasExistingLink(fileDownloadName);

      if (hasExistingLink && source === FileUploadSource.PREVIOUS_ATTACHMENT) {
        this.reuseExistingFileLink(blob, fileName, fileDownloadName);
        return;
      }

      const internalFile: IInternalFileUpload = {
        fileName: fileName,
        fileSize: blob.size,
        fileType: fileType,
        fileDownloadName: fileDownloadName,
        content: base64Content,
        source: source,
        uploadResponse: undefined,
      };

      this.uploadedFile = internalFile;
      this.cd.detectChanges();

      this.startInternalUpload(internalFile);
    };

    reader.onerror = () => {
      this.loadingFile = false;
      this.toastService.show({
        body: this.translateService.instant('Failed to process selected file'),
        type: 'error',
      });
      this.cd.detectChanges();
    };

    reader.readAsDataURL(blob);
  }

  handlePreviousAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.PREVIOUS_ATTACHMENTS);
  }

  handleCertificateAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.CERTIFICATES);
  }

  private handlePagination(
    event: Record<string, number>,
    type: UserDocumentType
  ) {
    const limit = event['scrollHeight'] - event['clientHeight'];
    const moreItems =
      type === UserDocumentType.CERTIFICATES
        ? this.totalCertificateAttachments !==
          this.certificateAttachments.length
        : this.totalPreviousAttachments !== this.previousAttachments.length;

    if (event['scrollPosition'] === limit && moreItems) {
      this.page++;
      if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
        this.loadUserDocuments(
          '/application/v1/application/find-user-previous-attachments',
          UserDocumentType.PREVIOUS_ATTACHMENTS
        );
      }
      if (type === UserDocumentType.CERTIFICATES) {
        this.loadUserDocuments(
          '/application/v1/application/find-user-certificates',
          UserDocumentType.CERTIFICATES
        );
      }
    }
  }

  private loadUserDocuments(endpoint: string, type: UserDocumentType) {
    const url = `${this.environment.apiGatewayBaseUrl}${endpoint}`;
    const { dataset } = this.field.props;
    const params: Record<string, unknown> = {
      page: this.page,
      size: this.size,
      sort: this.sort,
    };

    const keyString = dataset?.['dataField']
      ? dataset['dataField']
      : this.dataField;
    if (keyString) {
      const queryParams = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');

      this.subscriptions.add(
        this.http
          .get<Record<string, unknown>>(`${url}?${queryParams}`)
          .subscribe({
            next: (res: Record<string, unknown>) => {
              const data = res['data'] as Record<string, unknown>;
              const totalElements = data['totalElements'] as number;
              this.loadCertificates(type, res, keyString, totalElements);
              this.loadPreviousDocuments(type, res, keyString, totalElements);
              this.cd.detectChanges();
            },
            error: () => {
              this.toastService.show({
                body: `Error loading ${type}`,
                type: 'error',
              });
              this.cd.detectChanges();
            },
          })
      );
    }
  }

  private loadCertificates(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.CERTIFICATES) {
      this.totalCertificateAttachments = totalElements;
      const certificateAttachmentsData = Array.isArray(res)
        ? (res as ICertificateAttachmentEvent[])
        : this.getValueFromNestedObject<ICertificateAttachmentEvent>(
            res,
            keyString
          );
      this.certificateAttachments.push(...certificateAttachmentsData);
      // Construct the display name for the certificate attachments
      const locale = localStorage.getItem('locale') ?? 'en-US';
      this.certificateAttachments.forEach(attachment => {
        const serviceName = attachment.serviceName;
        const applicationNumber = attachment.certificateNames[locale]
          .split('-')
          .pop();
        attachment.displayName = `${serviceName} - ${applicationNumber}`;
      });
    }
  }

  private loadPreviousDocuments(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
      this.totalPreviousAttachments = totalElements;
      const previousAttachmentsData = Array.isArray(res)
        ? (res as IPreviousAttachmentEvent[])
        : this.getValueFromNestedObject<IPreviousAttachmentEvent>(
            res,
            keyString
          );
      this.previousAttachments.push(...previousAttachmentsData);
      // Construct the display name for the previous attachments
      this.previousAttachments.forEach(attachment => {
        const fileName = attachment.fileName;
        const applicationNumber = fileName.split('_')[0];
        const originalName = attachment.fileOriginalName;
        attachment.displayName = `${applicationNumber} - ${originalName}`;
      });
    }
  }

  private getValueFromNestedObject<T>(
    obj: Record<string, unknown>,
    keyString: string
  ): T[] {
    const keys = keyString.split('.');
    let value = obj;
    for (const key of keys) {
      value = value[key] as Record<string, unknown>;
      if (value === undefined) {
        throw new Error(`Invalid key string: ${keyString}`);
      }
    }
    return value as unknown as T[];
  }

  private validatePhoto(data: string) {
    const url = `${this.environment.apiGatewayBaseUrl}/photo-validation/api/photo/validate`;
    return this.http.post<Record<string, unknown>>(url, { data });
  }

  private inferMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.toLowerCase().split('.').pop();
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      webp: 'image/webp',
      txt: 'text/plain',
      csv: 'text/csv',
      xml: 'text/xml',
      zip: 'application/zip',
      rar: 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed',
    };
    return mimeTypes[extension || ''] || 'application/octet-stream';
  }

  /**
   * Find form control using the same logic as generic-data-fetch component
   * This allows accessing controls from any level of the form hierarchy
   */
  private findControl(targetKey: string): AbstractControl | null {
    // Check if we should search globally or within scope
    const searchGlobally =
      this.field.props?.['populateRelativeToRootForm'] !== false;

    return searchGlobally
      ? findFormControl(this.form, targetKey)
      : findFormControlInScope(this.form, targetKey);
  }

  /**
   * Get blob from file manager - same implementation as custom-file-upload
   */
  private getBlob(
    queryParams: Record<string, unknown>,
    fileManagerUrl: string
  ) {
    const params = Object.keys(queryParams)
      .map(key => `${key}=${encodeURIComponent(queryParams[key] as string)}`)
      .join('&');
    return this.http.get(`${fileManagerUrl}?${params}`, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopSessionKeepAlive();
  }
}
